/**
 * @typedef {import('../wizardSteps/wizardSteps').default} WizardSteps
 */

import tingle from 'tingle.js';
import { nextStep, previousStep } from '../../actions';
import { DOCUMENT_POSITION_PRECEDING, EVENT_CLOSE_RECEIPT, EVENT_NAME_FIELDS_VALIDATE } from '../../helpers/consts';
import MatchMedia from '../../helpers/matchMedia';
import MoveDomElement from '../../helpers/moveDomElement';
import { GtmDataLayer } from '../../data/gtmDataLayer';

export class StepNavigation {
    /**
     * Component that handles the step navigation, next and previous button.
     * @constructor
     * @param {Object} options
     * @param {Object} options.store
     * @param {HTMLElement} options.$context - The DOM element used as context
     * @param {WizardSteps} options.wizardSteps - Steps component element
     */
    constructor(options) {
        this.store = options.store;
        this.state = this.store.getState();
        this.steps = this.state.steps;
        this.$context = options.$context;
        this.$el = this.createStepNavigationElement();
        this.store.subscribe(this.update.bind(this));
        this.wizardSteps = options.wizardSteps;
        this.modal = this.createModal();
        this.modalActive = false;
        this.modalShown = false;
        this.translations = options.translations;

        this.gtmPushCheckout();
        this.update();
        MoveDomElement.setInitialDomTreeLocation(this.$previousButton);
        this.resizeWindow();
    }

    createStepNavigationElement() {
        this.$stepNavigationElem = document.createElement('nav');
        this.$stepNavigationElem.classList.add('step-navigation');

        this.$previousButton = this.createPreviousButton(__('Previous step'));
        this.$nextButton = this.createNextButton(this.state.steps[1].name);

        this.$stepNavigationElem.appendChild(this.$nextButton);
        this.$stepNavigationElem.appendChild(this.$previousButton);

        return this.$stepNavigationElem;
    }

    resizeWindow() {
        const current = this.getCurrentStep();

        if (current.hidden !== true) {
            this.movePreviousButton();
        }
        window.addEventListener('resize', () => {
            if (current.hidden !== true) {
                this.movePreviousButton();
            }
        });
    }

    movePreviousButton() {
        const $stepIndicator = document.getElementById('stepIndicator');

        if (MatchMedia.small()) {
            $stepIndicator.parentNode.insertBefore(
                this.$previousButton,
                $stepIndicator
            );
        } else {
            MoveDomElement.resetDomTreeLocation(this.$previousButton);
        }
    }

    createPreviousButton(label = '') {
        const $buttonElem = document.createElement('button');
        const $buttonInnerElem = document.createElement('span');
        const $buttonElemText = document.createTextNode(label);

        // Arrow-left icon: the same arrow glyph as the next button's
        // .button--arrow-css, mirrored horizontally so it points left. The DS
        // .button mixin sizes/colours the <svg> child automatically.
        const $iconWrapper = document.createElement('div');
        $iconWrapper.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" viewBox="0 0 24 24"><path transform="translate(24 0) scale(-1 1)" d="M16.175 13H4V11H16.175L10.575 5.4L12 4L20 12L12 20L10.575 18.6L16.175 13Z"/></svg>';
        const $iconElemPrev = $iconWrapper.firstElementChild;

        $buttonElem.classList.add('button', 'button--secondary');
        $buttonInnerElem.classList.add('button__text');
        $buttonInnerElem.appendChild($buttonElemText);
        $buttonElem.appendChild($iconElemPrev);
        $buttonElem.appendChild($buttonInnerElem);

        $buttonElem.addEventListener(
            'click',
            () => {
                const current = this.state.steps.find(
                    step => step.current === true
                );
                this.wizardSteps.currentStepIndex = parseInt(current.id.substring(5, 6), 0);
                if (this.wizardSteps.currentStepIndex > 1) {
                    this.wizardSteps.currentStepIndex -= 1;
                    this.store.dispatch(previousStep(this.wizardSteps.currentStepIndex));
                    this.gtmPushCheckout();
                }

                window.scrollTo(0, 85);
            },
            true
        );

        return $buttonElem;
    }

    createNextButton(label = '') {
        const $buttonElem = document.createElement('button');
        const $buttonInnerElem = document.createElement('span');
        const $buttonElemText = document.createTextNode(label);

        $buttonElem.classList.add('button', 'button--primary', 'button--arrow-css');
        $buttonInnerElem.classList.add('button__text');
        $buttonInnerElem.appendChild($buttonElemText);
        $buttonElem.appendChild($buttonInnerElem);

        document.addEventListener(
            'click',
            event => {
                const targetElem = event.target;
                const currentStep = this.getCurrentStep();
                const current = this.state.steps.find(
                    step => step.current === true
                );

                if (
                    ($buttonElem === targetElem ||
                        $buttonElem === targetElem.parentNode) &&
                    !$buttonElem.classList.contains('is-invalid')
                ) {
                    this.wizardSteps.currentStepIndex = parseInt(current.id.substring(5, 6), 0);

                    if (current.id === 'step_6') {
                        event.preventDefault();
                        if (typeof window.grecaptcha !== 'undefined') {
                            window.grecaptcha.ready(() => {
                                window.grecaptcha.execute(this.state.settings.recaptcha_sitekey, { action: 'submit' }).then((token) => {
                                    this.wizardSteps.submitOrder(token);
                                });
                            });
                        } else {
                            this.wizardSteps.submitOrder();
                        }

                        return false;
                    }

                    if (current.id === 'step_2' || current.id === 'step_3' || current.id === 'step_4' || current.id === 'step_5') {
                        this.wizardSteps.submitContact();
                    }

                    const nextStepId = `step_${this.wizardSteps.currentStepIndex + 1}`;
                    const nextStepObject = this.state.steps.find(
                        step => step.id === nextStepId
                    );

                    if (this.state.steps.length > this.wizardSteps.currentStepIndex) {
                        this.wizardSteps.currentStepIndex += 1;
                        this.store.dispatch(nextStep(this.wizardSteps.currentStepIndex));
                        this.gtmPushCheckout();
                    }

                    document.dispatchEvent(EVENT_CLOSE_RECEIPT);
                    this.focusField(nextStepObject);
                } else if (
                    (targetElem === this.$stepNavigationElem ||
                        targetElem === $buttonInnerElem ||
                        $buttonElem === targetElem) &&
                    $buttonElem.classList.contains('is-invalid')
                ) {
                    this.focusField(currentStep);
                    (current.id === 'step_6' ? document.querySelectorAll('#step_6_mainPanel .stepcontainer') : document.querySelectorAll('li.wizard-step--active .fieldset'))
                        .forEach(el => el.dispatchEvent(new Event(EVENT_NAME_FIELDS_VALIDATE)));
                }

                return false;
            },
            true
        );

        return $buttonElem;
    }

    updateNextButton(current) {
        const stepIndex = this.state.steps.findIndex(
            _step => _step.id === current.id
        );

        if (this.state.steps[stepIndex + 1]) {
            const nextButtonText = this.state.steps[stepIndex + 1].name;
            const stepValid = this.state.steps[stepIndex].valid;
            this.$nextButton.classList.remove('visually-hidden', 'is-valid', 'is-invalid');
            this.$nextButton.classList.add(stepValid ? 'is-valid' : 'is-invalid');
            this.$nextButton.title = !stepValid
                ? __('Enter all required fields')
                : '';
            this.$nextButton.querySelector('span').innerHTML = nextButtonText;
        } else {
            this.$nextButton.classList.add('visually-hidden');
        }
    }

    updatePreviousButton(current) {
        const stepIndex = this.state.steps.findIndex(
            _step => _step.id === current.id
        );

        if (stepIndex > 0) {
            this.$previousButton.disabled = false;
        } else {
            this.$previousButton.disabled = true;
        }
    }

    update() {
        const current = this.getCurrentStep();

        if (!this.modalShown && !this.modalActive && (this.translations.step_1_show_modal && this.translations.step_1_show_modal === '1') && (current.id === 'step_2' && current.previousStep.id === 'step_1')) {
            this.modalActive = true;
            this.modalShown = true;
            this.modal.setContent(`<div>${this.translations.step_1_modal}</div>`);
            this.modal.addFooterBtn(
                this.translations.step_1_modal_button,
                'btn btn-blue btn--next username-btn',
                () => {
                    this.modal.close();
                    this.modalActive = false;
                }
            );
            this.modal.open();
        }

        if (current.id === 'step_1') {
            this.modalShown = false;
        }

        if (current.hidden) {
            this.$context.remove();
            this.$previousButton.remove();
            this.$nextButton.remove();
            return;
        }
        this.updateNextButton(current);
        this.updatePreviousButton(current);
    }

    gtmPushCheckout() {
        GtmDataLayer.checkout(
            this.state.product,
            this.state.users,
            this.state.modules,
            this.state.extras,
            {
                name: this.state.steps.find(step => step.current === true).name,
                id: this.state.steps.find(step => step.current === true).id,
            },
            this.state.formFields
        );
    }

    gtmPusPurchase() {
        GtmDataLayer.purchase(this.state);
    }

    gtmPusFormFields() {
        GtmDataLayer.formFields(this.state);
    }

    getCurrentStep() {
        this.state = this.store.getState();
        const current = this.state.steps.find(step => step.current === true);
        return current;
    }

    // eslint-disable-next-line class-methods-use-this
    focusField(currentStep) {
        let $focusField = document.querySelector(
            `#${
                currentStep.id
            }_mainPanel div:not(.combined-field) > input[required]:not([type=hidden]):first-of-type`
        );
        const $focusRadioField = document.querySelector(
            `#${
                currentStep.id
            }_mainPanel div:not(.combined-field) > .field--radio > input[type=radio]:not([type=hidden]):not(:checked):first-of-type`
        );
        const $focusCombinedField = document.querySelector(
            `#${
                currentStep.id
            }_mainPanel div > .combined-field > input[required]:not([type=hidden]):first-of-type`
        );

        if (
            $focusField &&
            $focusCombinedField &&
            $focusField.compareDocumentPosition($focusCombinedField) ===
            DOCUMENT_POSITION_PRECEDING
        ) {
            $focusField = $focusCombinedField;
        } else if (
            $focusField &&
            $focusRadioField &&
            $focusField.compareDocumentPosition($focusRadioField) ===
            DOCUMENT_POSITION_PRECEDING
        ) {
            $focusField = $focusRadioField;
        }

        if ($focusField) {
            $focusField.focus();
        } else {
            window.scrollTo(0, 85);
        }
    }

    render() {
        return this.$el;
    }

    // eslint-disable-next-line class-methods-use-this
    handleErrors(response) {
        // eslint-disable-next-line no-alert
        if (!response || !response.status === 200) {
            throw Error(response ? response.status : 'No response');
        }
        return response;
    }


    // eslint-disable-next-line class-methods-use-this
    createModal() {
        // eslint-disable-next-line new-cap
        return new tingle.modal({
            closeMethods: [''],
            cssClass: ['extra-modal'],
            footer: true,
            beforeClose() {
                this.modalActive = false;
                this.setFooterContent('');
                return true;
            }
        });
    }
}

export default StepNavigation;
